import React, { useState, useEffect } from "react";
import { auth, db } from "../firebase-config";
import { createUserWithEmailAndPassword, sendPasswordResetEmail, signOut } from "firebase/auth";
import { collection, addDoc, getDocs, doc, setDoc } from "firebase/firestore";
import "./AddEmployeeForm.css";
import { useNavigate } from "react-router-dom";
import { app } from "../firebase-config";

function AddEmployeeForm() {
  const [prenom, setPrenom] = useState("");
  const [nom, setNom] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [sendResetEmail, setSendResetEmail] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const [selectedSites, setSelectedSites] = useState([]);
  const [sites, setSites] = useState([]);
  const [hasWorkingHours, setHasWorkingHours] = useState(false);
  const [weeklySchedule, setWeeklySchedule] = useState({});
  const [toleranceDebut, setToleranceDebut] = useState(0);
  const [toleranceFin, setToleranceFin] = useState(0);
  const [startWeekDay] = useState("Lundi");

  const [entreprise, setEntreprise] = useState("");
  const [siret, setSiret] = useState("");

  const days = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];

  useEffect(() => {
    fetchSites();
  }, []);

  const fetchSites = async () => {
    try {
      const snap = await getDocs(collection(db, "sites"));
      const siteList = snap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setSites(siteList);
    } catch (error) {
      console.error("Erreur lors de la récupération des sites:", error);
      alert("Erreur lors du chargement des sites de travail. Veuillez réessayer.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!prenom || !nom || !email || selectedSites.length === 0) {
      alert("Tous les champs sont obligatoires sauf le mot de passe !");
      return;
    }

    // Validation de l'email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      alert("Veuillez saisir une adresse email valide.");
      return;
    }

    setIsSubmitting(true);

    // Normaliser l'email : toujours en minuscules (Firebase Auth fait pareil)
    const normalizedEmail = email.trim().toLowerCase();

    // Sauvegarder l'utilisateur admin actuel pour se reconnecter après
    const currentUser = auth.currentUser;

    try {
      // Si aucun mot de passe n'est défini, en générer un aléatoire
      const tempPassword = password || Math.random().toString(36).slice(-12) + "Aa1!";

      // Créer l'utilisateur avec Firebase Auth (ça va déconnecter l'admin temporairement)
      const userCredential = await createUserWithEmailAndPassword(auth, normalizedEmail, tempPassword);
      const newUser = userCredential.user;
      
      // Créer la fiche employé dans Firestore
      await setDoc(doc(db, "employees", newUser.uid), {
        prenom,
        nom,
        email: normalizedEmail,
        role: 'employee',
        siteDeTravail: selectedSites,
        hasWorkingHours,
        weeklySchedule,
        toleranceDebut,
        toleranceFin,
        startWeekDay,
        entreprise,
        siret,
        createdAt: new Date()
      });

      // Déconnecter le nouvel utilisateur
      await signOut(auth);
      
      // L'admin devra se reconnecter manuellement
      
      // Envoyer l'email de réinitialisation si demandé
      if (sendResetEmail) {
        try {
          await sendPasswordResetEmail(auth, email);
        } catch (resetError) {
          console.warn("Erreur lors de l'envoi de l'email de réinitialisation:", resetError);
        }
      }

      // Réinitialiser le formulaire
      setPrenom("");
      setNom("");
      setEmail("");
      setPassword("");
      setSelectedSites([]);
      setHasWorkingHours(false);
      setWeeklySchedule({});
      setToleranceDebut(0);
      setToleranceFin(0);
      setEntreprise("");
      setSiret("");

      let message = "✅ Salarié ajouté avec succès !";
      if (!password) {
        message += `\n\n🔑 Mot de passe temporaire généré : ${tempPassword}\n⚠️ Notez ce mot de passe, il ne sera plus affiché !`;
      }
      if (sendResetEmail) {
        message += "\n\n📧 Un email de réinitialisation du mot de passe a été envoyé.";
      }
      message += "\n\n🔄 Vous allez être déconnecté. Reconnectez-vous pour continuer.";

      alert(message);
      
      // Rediriger vers la page de connexion
      navigate("/");
    } catch (error) {
      console.error("Erreur création utilisateur :", error);
      
      // Messages d'erreur plus spécifiques
      let errorMessage = "Erreur lors de la création : ";
      if (error.code === 'auth/email-already-in-use') {
        errorMessage += "Cette adresse email est déjà utilisée.";
      } else if (error.code === 'auth/invalid-email') {
        errorMessage += "Adresse email invalide.";
      } else if (error.code === 'auth/weak-password') {
        errorMessage += "Le mot de passe est trop faible.";
      } else if (error.message.includes('permission-denied')) {
        errorMessage += "Vous n'avez pas les permissions nécessaires pour créer un utilisateur.";
      } else {
        errorMessage += error.message;
      }
      
      alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="add-employee-form" onSubmit={handleSubmit}>
      <div className="form-group">
        <label>Nom</label>
        <input type="text" value={nom} onChange={e => setNom(e.target.value)} required />
      </div>
      <div className="form-group">
        <label>Prénom</label>
        <input type="text" value={prenom} onChange={e => setPrenom(e.target.value)} required />
      </div>
      <div className="form-group">
        <label>Entreprise</label>
        <input type="text" value={entreprise} onChange={e => setEntreprise(e.target.value)} maxLength={64} placeholder="Nom de l'entreprise (facultatif)" />
      </div>
      <div className="form-group">
        <label>SIRET</label>
        <input type="text" value={siret} onChange={e => setSiret(e.target.value)} maxLength={14} placeholder="Numéro SIRET (facultatif)" />
      </div>
      <input
        type="email"
        placeholder="Email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />

      <div className="password-section">
        <input
          type="password"
          placeholder="Mot de passe temporaire (si vide, un mot de passe sera généré)"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <label className="reset-option">
          <input
            type="checkbox"
            checked={sendResetEmail}
            onChange={(e) => setSendResetEmail(e.target.checked)}
          />
          Envoyer un email de réinitialisation du mot de passe
        </label>
      </div>

      <label>Sites de travail :</label>
      <select
        multiple
        value={selectedSites}
        onChange={(e) => setSelectedSites(Array.from(e.target.selectedOptions, option => option.value))}
        className="multi-select"
      >
        {sites.map((site) => (
          <option key={site.id} value={site.id}>
            {site.nom} ({site.rayon} m)
          </option>
        ))}
      </select>

      <div className="horaires-section">
        <p style={{ fontSize: "0.9em", marginBottom: "5px" }}>
          📌 <strong>Horaires</strong> : définissez les horaires de travail pour chaque jour de la semaine.
        </p>
        <p style={{ fontSize: "0.9em", marginBottom: "10px" }}>
          📌 <strong>Tolérances</strong> : définissez une marge de tolérance en minutes :<br />
          • Tolérance début : si le salarié arrive dans cette marge avant l'heure prévue, le temps n'est pas compté. Au-delà, tout est compté.<br />
          • Tolérance fin : si le salarié part dans cette marge après l'heure prévue, le temps n'est pas compté. Au-delà, tout est compté.
        </p>

        <label>
          <input
            type="checkbox"
            checked={hasWorkingHours}
            onChange={() => {
              setHasWorkingHours(!hasWorkingHours);
              if (!hasWorkingHours) {
                setToleranceDebut(0);
                setToleranceFin(0);
              }
            }}
          />
          Activer les horaires de travail
        </label>

        {hasWorkingHours && (
          <div className="horaires-table">
            <table>
              <thead>
                <tr>
                  <th>Jour</th>
                  <th>Heure Début</th>
                  <th>Heure Fin</th>
                </tr>
              </thead>
              <tbody>
                {days.map((day) => (
                  <tr key={day}>
                    <td>{day}</td>
                    <td>
                      <input
                        type="time"
                        value={weeklySchedule[day]?.debut || ""}
                        onChange={(e) =>
                          setWeeklySchedule((prev) => ({
                            ...prev,
                            [day]: {
                              ...prev[day],
                              debut: e.target.value,
                            },
                          }))
                        }
                      />
                    </td>
                    <td>
                      <input
                        type="time"
                        value={weeklySchedule[day]?.fin || ""}
                        onChange={(e) =>
                          setWeeklySchedule((prev) => ({
                            ...prev,
                            [day]: {
                              ...prev[day],
                              fin: e.target.value,
                            },
                          }))
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="tolerances">
              <h4>Tolérances</h4>
              <label>
                Tolérance début (minutes) :
                <input
                  type="number"
                  min="0"
                  value={toleranceDebut}
                  onChange={(e) => setToleranceDebut(Number(e.target.value))}
                />
              </label>
              <label>
                Tolérance fin (minutes) :
                <input
                  type="number"
                  min="0"
                  value={toleranceFin}
                  onChange={(e) => setToleranceFin(Number(e.target.value))}
                />
              </label>
            </div>
          </div>
        )}
      </div>

      <div className="buttons-container">
        <button type="submit" className="submit-button" disabled={isSubmitting}>💾 Ajouter</button>
        <button type="button" className="cancel-button" onClick={() => navigate("/admin/employees")}>
          Annuler
        </button>
      </div>
    </form>
  );
}

export default AddEmployeeForm;